import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLocalization } from "../contexts/LocalizationContext";
import {
  Mail,
  AlertCircle,
  Globe,
  ChevronDown,
  Shield,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  KeyRound,
} from "lucide-react";

/* ── Inline SVG: Lock with key ── */
const LockKeySvg = () => (
  <svg viewBox="0 0 200 220" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
    {/* Lock body */}
    <rect x="55" y="100" width="90" height="70" rx="12" fill="#e0e7ff" stroke="#6366f1" strokeWidth="2"/>
    {/* Lock shackle */}
    <path d="M75 100 V75 Q75 45 100 45 Q125 45 125 75 V100" stroke="#6366f1" strokeWidth="3" fill="none" strokeLinecap="round"/>
    {/* Keyhole */}
    <circle cx="100" cy="130" r="10" fill="#c7d2fe" stroke="#6366f1" strokeWidth="1.5"/>
    <rect x="97" y="135" width="6" height="15" rx="2" fill="#c7d2fe" stroke="#6366f1" strokeWidth="1.5"/>
    {/* Key */}
    <g transform="translate(140, 80) rotate(30)">
      <circle cx="0" cy="0" r="12" fill="#fde68a" stroke="#f59e0b" strokeWidth="1.5"/>
      <circle cx="0" cy="0" r="5" fill="white" stroke="#f59e0b" strokeWidth="1"/>
      <rect x="10" y="-3" width="30" height="6" rx="2" fill="#fde68a" stroke="#f59e0b" strokeWidth="1.5"/>
      <rect x="32" y="-3" width="4" height="10" rx="1" fill="#fde68a" stroke="#f59e0b" strokeWidth="1"/>
      <rect x="38" y="-3" width="4" height="8" rx="1" fill="#fde68a" stroke="#f59e0b" strokeWidth="1"/>
    </g>
    {/* Sparkles */}
    <circle cx="45" cy="85" r="3" fill="#a7f3d0" opacity="0.6"/>
    <circle cx="160" cy="140" r="2" fill="#c7d2fe" opacity="0.5"/>
    <circle cx="50" cy="180" r="2.5" fill="#fde68a" opacity="0.5"/>
  </svg>
);

/* ── Inline SVG: Email sent ── */
const EmailSentSvg = () => (
  <svg viewBox="0 0 200 180" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
    {/* Envelope */}
    <rect x="30" y="50" width="140" height="90" rx="10" fill="#e0e7ff" stroke="#6366f1" strokeWidth="2"/>
    {/* Envelope flap */}
    <path d="M30 55 L100 100 L170 55" stroke="#6366f1" strokeWidth="2" fill="#ddd6fe" strokeLinejoin="round"/>
    {/* Letter peeking out */}
    <rect x="50" y="30" width="100" height="50" rx="4" fill="white" stroke="#c7d2fe" strokeWidth="1.5"/>
    <rect x="62" y="42" width="60" height="4" rx="2" fill="#e5e7eb"/>
    <rect x="62" y="52" width="40" height="3" rx="1.5" fill="#f3f4f6"/>
    <rect x="62" y="60" width="50" height="3" rx="1.5" fill="#f3f4f6"/>
    {/* Check mark */}
    <circle cx="155" cy="35" r="14" fill="#d1fae5" stroke="#10b981" strokeWidth="2"/>
    <path d="M148 35 l4 4 8-8" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    {/* Motion lines */}
    <path d="M20 90 L10 90" stroke="#6366f1" strokeWidth="1.5" strokeLinecap="round" opacity="0.3"/>
    <path d="M18 80 L8 75" stroke="#6366f1" strokeWidth="1.5" strokeLinecap="round" opacity="0.2"/>
    <path d="M18 100 L8 105" stroke="#6366f1" strokeWidth="1.5" strokeLinecap="round" opacity="0.2"/>
  </svg>
);

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const { t, language, setLanguage } = useLocalization();

  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    try {
      const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3944";
      const res = await fetch(`${API_URL}/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      // Backend always returns 200 with a generic message (anti-enumeration)
      setSuccess(true);
    } catch {
      setError(language === "fr"
        ? "Une erreur est survenue. Veuillez reessayer."
        : "An error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col relative overflow-hidden">
      {/* Background dots */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
        backgroundImage: "radial-gradient(circle at 1px 1px, rgba(99,102,241,0.4) 1px, transparent 0)",
        backgroundSize: "32px 32px",
      }} />

      {/* Top bar */}
      <div className="relative z-10 flex items-center justify-between px-6 sm:px-10 py-5">
        <div className="flex items-center gap-2.5">
          <img src="/LOGO_MOKO_fd_vert.png" alt="MOKO Afrika" className="h-10 w-10 rounded-xl shadow-md object-cover"/>
          <div className="flex flex-col">
            <span className="text-sm font-bold text-slate-900 leading-tight">MOKO Afrika</span>
            <span className="text-[9px] text-indigo-500 font-bold tracking-[0.15em] uppercase">Merchant Suite</span>
          </div>
        </div>

        {/* Language selector */}
        <div className="relative z-50">
          <button
            type="button"
            onClick={() => setLangMenuOpen(!langMenuOpen)}
            className="flex items-center gap-2 px-3.5 py-2 rounded-xl border border-slate-200 hover:border-slate-300 hover:bg-slate-50 bg-white text-sm font-medium text-slate-600 hover:text-slate-900 transition-all shadow-sm"
          >
            <Globe className="w-4 h-4" />
            <span>{language === "fr" ? "FR" : "EN"}</span>
            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${langMenuOpen ? "rotate-180" : ""}`} />
          </button>
          {langMenuOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setLangMenuOpen(false)} />
              <div className="absolute right-0 mt-2 w-44 bg-white border border-slate-200 rounded-xl shadow-xl z-50 py-1 overflow-hidden">
                <button type="button" onClick={() => { setLanguage("fr"); setLangMenuOpen(false); }}
                  className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-3 transition-colors ${language === "fr" ? "bg-indigo-50 text-indigo-700 font-semibold" : "text-slate-700 hover:bg-slate-50"}`}>
                  <span className="text-base">🇫🇷</span> {t("language.french") || "Francais"}
                </button>
                <button type="button" onClick={() => { setLanguage("en"); setLangMenuOpen(false); }}
                  className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-3 transition-colors ${language === "en" ? "bg-indigo-50 text-indigo-700 font-semibold" : "text-slate-700 hover:bg-slate-50"}`}>
                  <span className="text-base">🇬🇧</span> {t("language.english") || "English"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Main content */}
      <div className="relative z-10 flex-1 flex items-center justify-center px-4 pb-10">
        {/* Illustrations */}
        <div className="hidden lg:block absolute left-[12%] top-[20%] w-44 h-48 opacity-50 hover:opacity-80 transition-opacity duration-500">
          <LockKeySvg />
        </div>
        <div className="hidden lg:block absolute right-[12%] top-[25%] w-48 h-44 opacity-50 hover:opacity-80 transition-opacity duration-500">
          <EmailSentSvg />
        </div>

        {/* Form card */}
        <div className="w-full max-w-[420px] bg-white rounded-2xl shadow-xl shadow-slate-200/60 border border-slate-100 p-8 sm:p-10">
          {success ? (
            /* Success state */
            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-5">
                <CheckCircle2 className="w-8 h-8 text-emerald-500" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2 tracking-tight">
                {language === "fr" ? "Email envoye !" : "Email sent!"}
              </h1>
              <p className="text-slate-500 text-sm mb-6 leading-relaxed">
                {language === "fr"
                  ? "Si un compte existe avec cette adresse, vous recevrez un lien de reinitialisation dans quelques minutes."
                  : "If an account exists with this email, you'll receive a reset link in a few minutes."}
              </p>
              <button
                onClick={() => navigate("/login")}
                className="w-full py-3.5 rounded-xl font-semibold text-[15px] text-white flex items-center justify-center gap-2 group"
                style={{ background: 'linear-gradient(135deg, #667eea 0%, #5a67d8 100%)' }}
              >
                <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                <span>{language === "fr" ? "Retour a la connexion" : "Back to login"}</span>
              </button>
            </div>
          ) : (
            /* Form state */
            <>
              <div className="text-center mb-8">
                <div className="w-14 h-14 bg-indigo-50 rounded-full flex items-center justify-center mx-auto mb-5">
                  <KeyRound className="w-7 h-7 text-indigo-500" />
                </div>
                <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2 tracking-tight">
                  {language === "fr" ? "Mot de passe oublie ?" : "Forgot password?"}
                </h1>
                <p className="text-slate-500 text-sm leading-relaxed">
                  {language === "fr"
                    ? "Entrez votre adresse email et nous vous enverrons un lien pour reinitialiser votre mot de passe."
                    : "Enter your email address and we'll send you a link to reset your password."}
                </p>
              </div>

              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-xl flex items-start gap-3">
                  <div className="mt-0.5 w-5 h-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                    <AlertCircle className="h-3.5 w-3.5 text-red-600" />
                  </div>
                  <span className="text-sm text-red-700 leading-snug">{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-[13px] font-semibold text-slate-700 mb-2">
                    {language === "fr" ? "Adresse email" : "Email address"}
                  </label>
                  <div className="relative group">
                    <div className="absolute left-0 top-0 bottom-0 w-12 flex items-center justify-center pointer-events-none">
                      <Mail className="w-[18px] h-[18px] text-slate-400 group-focus-within:text-indigo-600 transition-colors" />
                    </div>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-12 pr-4 py-3.5 bg-slate-50/80 border border-slate-200 rounded-xl text-[15px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 focus:bg-white transition-all"
                      placeholder={language === "fr" ? "votre@email.com" : "your@email.com"}
                      required
                      autoComplete="email"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full relative py-3.5 rounded-xl font-semibold text-[15px] text-white shadow-lg shadow-indigo-500/25 hover:shadow-xl hover:shadow-indigo-500/35 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0 flex items-center justify-center gap-2 group overflow-hidden"
                  style={{ background: 'linear-gradient(135deg, #667eea 0%, #5a67d8 100%)' }}
                >
                  <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                  {isLoading ? (
                    <>
                      <svg className="animate-spin h-5 w-5 relative" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      <span className="relative">{language === "fr" ? "Envoi en cours..." : "Sending..."}</span>
                    </>
                  ) : (
                    <>
                      <span className="relative">{language === "fr" ? "Envoyer le lien" : "Send reset link"}</span>
                      <ArrowRight className="w-4 h-4 relative group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </button>
              </form>

              <button
                onClick={() => navigate("/login")}
                className="w-full mt-4 py-3 rounded-xl font-medium text-[14px] text-slate-600 hover:text-slate-900 hover:bg-slate-50 transition-all flex items-center justify-center gap-2 group"
              >
                <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                <span>{language === "fr" ? "Retour a la connexion" : "Back to login"}</span>
              </button>

              <div className="mt-6 flex items-center justify-center gap-2 text-[11px] text-slate-400">
                <Shield className="w-3 h-3" />
                <span>{t("login.encrypted") || "SSL Encrypted"}</span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="relative z-10 text-center pb-5 text-xs text-slate-400">
        &copy; {new Date().getFullYear()} MOKO Afrika. {t("login.allRightsReserved") || "All rights reserved."}
      </div>
    </div>
  );
};

export default ForgotPassword;
